// Where a document came from, and the one rule that hangs off it.
//
// CONSUMER: shared (Workers routes + web prep screen + node-pool tests).
//
// Every PDF in the OS used to be one we produced. A form out of the
// library, a packet we assembled, a letter an advisor wrote. We drew the
// boxes on it, on paper we control, and nobody needed to ask where the
// file came from because there was only ever one answer.
//
// A counteroffer breaks that. The listing agent writes it, fills it in,
// signs their side, and emails a PDF. It is somebody else's paper with
// somebody else's content on it, and the only thing we add is a set of
// boxes for our buyer to sign — boxes placed by the suggestion ladder
// (field-suggestions.ts), which is to say, guessed. A guess about where
// a signature line sits on a form we have never seen is a different
// thing from a saved map of a form we have mapped 324 blanks of, and
// pretending otherwise is how a buyer signs the wrong line.
//
// So `origin` is not a label. It is the switch behind the page-by-page
// confirmation: a received document cannot be sent until a person has
// looked at each page and said the boxes are in the right places.
//
// NULL means ours. Every row that existed before 2026-09-17 is NULL,
// because until then there was no way for a counterparty's file to get
// in, and backfilling tens of thousands of rows to assert something
// nobody checked is worse than a null that reads correctly.
//
// Pure and dependency-free, the field-types.ts rule: the Worker, the
// browser and the node-pool tests all answer this question the same way.

/** What a stored document is, as far as authorship goes. */
export type DocumentOrigin = "ours" | "received";

/** Read the column. NULL, empty, and anything unrecognised read as
 *  "ours" — the safe answer, because the only behaviour that hangs off
 *  "received" is EXTRA friction. A row we cannot classify should not
 *  silently skip a review; it should behave like the ordinary case it
 *  almost certainly is. */
export function documentOrigin(value: string | null | undefined): DocumentOrigin {
  return value?.trim() === "received" ? "received" : "ours";
}

/** Did the other side write this? The question every caller actually
 *  has, spelled once so no route re-derives it from a string compare. */
export function isReceivedDocument(value: string | null | undefined): boolean {
  return documentOrigin(value) === "received";
}

/** The words shown beside a received document, everywhere it appears.
 *
 *  Deliberately about the PEOPLE rather than the data ("They sent this"
 *  rather than "origin: received"): an agent scanning an offer needs to
 *  know at a glance which of four PDFs is the one that arrived, and
 *  nobody reads a column name at a glance. */
export const RECEIVED_DOCUMENT_BADGE = "They sent this";

/** Why a received document is treated differently, in one sentence, for
 *  the tooltip beside that badge. Lives here rather than in a component
 *  so the prep screen and the offers pane cannot drift into two
 *  different explanations of the same rule. */
export const RECEIVED_DOCUMENT_EXPLANATION =
  "This came from the other side, so the OS placed our signing boxes onto their form. Check each page before it goes out.";
